import { Container, Typography, Box, Grid2 as Grid, Alert, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BasePage from '../BasePage';
import AdminLayout from '../../components/AdminLayout';
import { useAdminDashboard } from '../../hooks/admin/useAdminDashboard';
import MetricsOverview from '../../components/admin/dashboard/MetricsOverview';
import UserGrowthChart from '../../components/admin/dashboard/UserGrowthChart';
import SubscriptionChart from '../../components/admin/dashboard/SubscriptionChart';
import ActivityFeed from '../../components/admin/dashboard/ActivityFeed';
import QuickActions from '../../components/admin/dashboard/QuickActions';

/**
 * AdminDashboard - System admin console landing page
 *
 * Section 1: Dashboard
 * - Key metrics overview (total users, active homes, MRR, businesses)
 * - User growth chart (30 days)
 * - Subscription distribution chart
 * - Recent activity feed
 * - Quick actions sidebar
 *
 * Layout:
 * - Top: 4 metric cards in responsive grid
 * - Middle: 2 charts side-by-side (user growth + subscription distribution)
 * - Bottom: Activity feed (left 2/3) + Quick actions (right 1/3)
 */
export default function AdminDashboard() {
  const {
    metrics,
    userGrowth,
    subscriptionDist,
    activityFeed,
    isLoading,
    error,
    refreshDashboard,
  } = useAdminDashboard();

  return (
    <BasePage
      title="Admin Dashboard | CribCritic"
      description="System administrator dashboard"
      keywords="admin, dashboard, analytics"
    >
      <AdminLayout>
        <Container maxWidth="xl">
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                System Admin Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Platform overview and key metrics
              </Typography>
            </Box>
            <IconButton
              onClick={refreshDashboard}
              disabled={isLoading}
              color="primary"
              sx={{
                '&:hover': {
                  transform: 'rotate(180deg)',
                  transition: 'transform 0.3s',
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Metrics Overview - 4 cards */}
          <Box sx={{ mb: 4 }}>
            <MetricsOverview metrics={metrics} loading={isLoading} />
          </Box>

          {/* Charts Section - User Growth + Subscription Distribution */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <UserGrowthChart data={userGrowth} loading={isLoading} />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <SubscriptionChart data={subscriptionDist} loading={isLoading} />
            </Grid>
          </Grid>

          {/* Bottom Section - Activity Feed + Quick Actions */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <ActivityFeed activities={activityFeed} loading={isLoading} />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <QuickActions loading={isLoading} />
            </Grid>
          </Grid>
        </Container>
      </AdminLayout>
    </BasePage>
  );
}
